/**
 * Étapes de la visite guidée du back-office. Une étape met en avant l'élément
 * portant `data-tour="<cible>"` ; sans cible (ou si l'élément est masqué, sur
 * un petit écran par exemple), la bulle s'affiche au centre.
 */
export interface EtapeVisite {
  id: string;
  cible?: string;
  titre: string;
  texte: string | ((superAdmin: boolean) => string);
}

export interface EtapeAffichee {
  id: string;
  cible?: string;
  titre: string;
  texte: string;
}

export const ETAPES_VISITE: EtapeVisite[] = [
  {
    id: 'bienvenue',
    titre: 'Bienvenue dans le nouveau back-office',
    texte: 'Voici en une minute où se trouve chaque chose. Vous pourrez relancer cette visite à tout moment depuis « Mon compte ».',
  },
  {
    id: 'recherche',
    cible: 'recherche',
    titre: 'Tout retrouver',
    texte: 'Tapez le nom ou l’e-mail d’une élève, une clé ou le nom d’un écran. Raccourci : Ctrl K (⌘ K sur Mac).',
  },
  {
    id: 'pilotage',
    cible: 'nav-pilotage',
    titre: 'Pilotage',
    texte:
      'Le tableau de bord réunit les chiffres clés et les points à traiter. Analytics détaille l’usage par jour, par coach, par module et par cohorte.',
  },
  {
    id: 'eleves',
    cible: 'nav-eleves',
    titre: 'Élèves',
    texte: (superAdmin) =>
      'Chaque demande se traite depuis la fiche de l’élève : crédits, coachs, type de compte, e-mails. « Crédits » regroupe tous les mouvements (avec la raison des générations échouées), l’ajout groupé et le prix de chaque génération' +
      (superAdmin ? ' (onglet « Prix des générations »).' : ', modifiable par un super admin.') +
      ' « Conversations, mémoires » : consultation tracée, avec un motif.',
  },
  {
    id: 'acces',
    cible: 'nav-acces',
    titre: 'Accès',
    texte:
      'Les clés d’accès pour l’inscription, les clés de déblocage des coachs, et les événements (Discoveur, Immersion) avec leur calendrier d’ouverture.',
  },
  {
    id: 'contenus',
    cible: 'nav-contenus',
    titre: 'Contenus',
    texte: (superAdmin) =>
      'Les modules, les coachs et leurs prompts. Chaque publication crée une version : on compare, on restaure, et on teste un prompt dans une conversation d’essai avant de le publier.' +
      (superAdmin ? '' : ' Consultation seulement : les modifications sont réservées aux super admins.'),
  },
  {
    id: 'ventes',
    cible: 'nav-ventes',
    titre: 'Ventes',
    texte: (superAdmin) =>
      'Packs de crédits, abonnements, abonnées et paiements, avec l’export pour la comptabilité.' +
      (superAdmin ? '' : ' Les offres et leurs prix se modifient par un super admin.'),
  },
  {
    id: 'systeme',
    cible: 'nav-systeme',
    titre: 'Système',
    texte: (superAdmin) =>
      (superAdmin ? '« Administrateurs » : inviter, changer un rôle, désactiver un compte. ' : '') +
      '« Journal d’audit » : qui a fait quoi, quand et sur quel objet.',
  },
  {
    id: 'compte',
    cible: 'mon-compte',
    titre: 'Mon compte',
    texte: 'Activez la double authentification, connectez un assistant IA (Claude) au back-office, et relancez cette visite.',
  },
  {
    id: 'fin',
    titre: 'À vous de jouer',
    texte: 'Chaque action est inscrite au journal d’audit à votre nom. Bonne prise en main !',
  },
];

/** Étapes telles qu'affichées pour ce rôle. */
export function etapesPour(superAdmin: boolean): EtapeAffichee[] {
  return ETAPES_VISITE.map((e) => ({
    id: e.id,
    ...(e.cible ? { cible: e.cible } : {}),
    titre: e.titre,
    texte: typeof e.texte === 'function' ? e.texte(superAdmin) : e.texte,
  }));
}

export interface Rectangle {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type Cote = 'droite' | 'dessous' | 'dessus' | 'gauche' | 'centre';

const MARGE = 12;
const ECART = 14;

/**
 * Place la bulle à côté de l'élément mis en avant : à droite, sinon dessous,
 * dessus ou à gauche, toujours dans la fenêtre ; au centre sans élément ou
 * faute de place.
 */
export function placerBulle(
  cible: Rectangle | null,
  bulle: { width: number; height: number },
  vue: { width: number; height: number }
): { top: number; left: number; cote: Cote } {
  const borne = (v: number, min: number, max: number) => Math.max(min, Math.min(v, max));
  const centre = {
    top: Math.max(MARGE, (vue.height - bulle.height) / 2),
    left: Math.max(MARGE, (vue.width - bulle.width) / 2),
    cote: 'centre' as const,
  };
  if (!cible) return centre;

  const vertical = borne(cible.top + cible.height / 2 - bulle.height / 2, MARGE, vue.height - bulle.height - MARGE);
  const horizontal = borne(cible.left + cible.width / 2 - bulle.width / 2, MARGE, vue.width - bulle.width - MARGE);

  if (cible.left + cible.width + ECART + bulle.width <= vue.width - MARGE) {
    return { top: vertical, left: cible.left + cible.width + ECART, cote: 'droite' };
  }
  if (cible.top + cible.height + ECART + bulle.height <= vue.height - MARGE) {
    return { top: cible.top + cible.height + ECART, left: horizontal, cote: 'dessous' };
  }
  if (cible.top - ECART - bulle.height >= MARGE) {
    return { top: cible.top - ECART - bulle.height, left: horizontal, cote: 'dessus' };
  }
  if (cible.left - ECART - bulle.width >= MARGE) {
    return { top: vertical, left: cible.left - ECART - bulle.width, cote: 'gauche' };
  }
  return centre;
}
